import { useEffect, useState } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Select
} from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import { MDTypography, MDButton, MDBox, CloseButton } from '@components/index';
import { useSelector } from 'react-redux';
import { RootState } from '@store/store';
import { MSG_EVENT, NotificationData } from '@DStypes/notifications';
import { useUpdateNotificationAsReadMutation } from '@api/notifications';
import { formatTitle, parseJSON } from '@utils/common';
import useToast from '@hooks/useToast';
import reactStringReplace from 'react-string-replace';
import { sendAmplitudeData } from '@utils/amplitude';
import {
  AUTO_CATEGORIZATION_FAILED,
  CLICKED_KNOW_MORE,
  SUBMIT_AUTO_CATEGORIZATION
} from '@constants/eventNames';

const useStyles = makeStyles(() => ({
  customWidth: {
    minWidth: '70px',
    maxWidth: '70px'
  }
}));

const AutoCategorizationModal = ({
  onClose,
  columnOptions,
  onCompleteCategorization,
  onInitiateAutoCategorization,
  isAutoCategorizationLoading,
  isAutoCategorizationOpen,
  chartData,
  segmentField,
  isSelfChart,
  chartId
}: {
  onClose: () => void;
  columnOptions: string[];
  onCompleteCategorization: (columnId: string, selectedColumn: string) => void;
  onInitiateAutoCategorization: (column: string, count: number) => void;
  isAutoCategorizationLoading: boolean;
  isAutoCategorizationOpen: boolean;
  segmentField: string;
  chartData: any;
  isSelfChart: string;
  chartId: string;
}) => {
  const classes = useStyles();
  const CATEGORY_COUNT = [5, 10, 15, 20, 25];
  const [selectedColumn, setSelectedColumn] = useState<string>(
    columnOptions[0]
  );
  const [categorizationCount, setCategorizationCount] = useState<number>(
    CATEGORY_COUNT[1]
  );
  const [uniqueValues, setUniqueValues] = useState<string[]>([]);

  const { pushErrorToast } = useToast();

  const notifications = useSelector(
    (state: RootState) => state?.notificationsData
  );

  const userDetails = useSelector((state: RootState) => state?.userDetails);

  const [markAsRead] = useUpdateNotificationAsReadMutation();

  const getNumberOptions = () => {
    const numberOptions: any[] = [];

    CATEGORY_COUNT.forEach((option: any) => {
      if (uniqueValues.length > Number(option))
        numberOptions.push(
          <MenuItem value={option} key={option} className={classes.customWidth}>
            <MDTypography color='secondary' variant='sm'>
              {option}
            </MDTypography>
          </MenuItem>
        );
    });

    return numberOptions.length ? (
      numberOptions
    ) : (
      <MenuItem
        key={CATEGORY_COUNT[0]}
        value={CATEGORY_COUNT[0]}
        className={classes.customWidth}
      >
        {CATEGORY_COUNT[0]}
      </MenuItem>
    );
  };

  // hook to handle success/failure of auto-categorization bassed on notification
  useEffect(() => {
    const handleComplete = async () => {
      if (notifications.unreadMessages?.length) {
        const messages: NotificationData[] = [];
        const errMessages: NotificationData[] = [];

        notifications.unreadMessages.forEach((notification) => {
          if (
            notification.message_event ===
            MSG_EVENT.FIELD_CATEGORISATION_SUCCESS
          )
            messages.push(notification);
          if (
            notification.message_event === MSG_EVENT.FIELD_CATEGORISATION_FAILED
          )
            errMessages.push(notification);
        });

        if (errMessages && errMessages?.[0]) {
          const messageData = errMessages[0].message_text;

          const htmlMsg = reactStringReplace(messageData, '\n', () => <br />);

          await markAsRead({
            id: userDetails.user.id as string,
            message_id: [...errMessages.map((message) => message.id)]
          });
          pushErrorToast(<>{htmlMsg}</>);
          onCompleteCategorization('', '');
          sendAmplitudeData(AUTO_CATEGORIZATION_FAILED);
        } else if (
          messages &&
          messages?.[0] &&
          parseJSON<any>(messages[0].message_data).old_col_name ===
            selectedColumn &&
          parseJSON<any>(messages[0].message_data).chart_id === chartId
        ) {
          // const messageData = messages[0].message_text;

          // const htmlMsg = reactStringReplace(messageData, '\n', () => <br />);
          // const finalMessage = reactStringReplace(
          //   htmlMsg,
          //   'You can now:',
          //   () => <span style={{ fontWeight: 'bold' }}>You can now:</span>
          // );

          // await markAsRead({
          //   id: userDetails.user.id as string,
          //   message_id: [messages?.[0]?.id as string]
          // });
          // pushSuccessToast(<>{finalMessage}</>);
          onCompleteCategorization(
            parseJSON<any>(messages[0].message_data).new_col_id,
            selectedColumn
          );
        }
      }
    };

    handleComplete();
  }, [
    isAutoCategorizationLoading,
    isAutoCategorizationOpen,
    notifications.unreadMessages
  ]);

  // hook to initialise/update unique values based on selected column
  useEffect(() => {
    let uniqueValues = [];

    if (segmentField && segmentField === selectedColumn) {
      const columnFields = Object.keys(chartData?.[0]);

      columnFields.splice(0, 1);
      uniqueValues = columnFields;
    } else {
      uniqueValues = chartData?.map((data: any) => data?.[selectedColumn]);
    }

    setCategorizationCount(
      uniqueValues.length > 10 ? CATEGORY_COUNT[1] : CATEGORY_COUNT[0]
    );
    setUniqueValues(uniqueValues);
  }, [selectedColumn]);

  return (
    <Dialog
      open={true}
      onClose={() => null}
      fullWidth
      sx={{
        zIndex: 200,
        '& .MuiPaper-root': {
          width: '700px',
          maxWidth: '800px',
          position: 'relative',
          overflow: 'visible'
        }
      }}
    >
      <CloseButton
        onClose={() => (isAutoCategorizationLoading ? null : onClose())}
      />
      <DialogTitle sx={{ px: '20px', my: '10px' }}>
        <MDTypography variant='h3' color='secondary' textGradient>
          Confirm Auto-Categorization
        </MDTypography>
      </DialogTitle>
      <DialogContent sx={{ px: '40px', width: '100%', mb: 2 }}>
        <MDBox sx={{ width: '100%' }}>
          <MDBox sx={{ width: '100%', mb: 5 }}>
            <MDBox display='flex' alignItems='center' sx={{ width: '100%' }}>
              <MDTypography variant='sm' color='sm'>
                Please confirm the auto-categorization for
              </MDTypography>
              <Select
                value={selectedColumn}
                onChange={(e: any) => setSelectedColumn(e.target.value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '250px',
                  maxWidth: '250px',
                  mx: 1,
                  py: 1
                }}
                renderValue={() => (
                  <>
                    {selectedColumn.length > 30
                      ? formatTitle(selectedColumn, 30)
                      : selectedColumn}
                  </>
                )}
                disabled={isAutoCategorizationLoading}
              >
                {columnOptions?.map((option: any) => (
                  <MenuItem value={option} key={option}>
                    <MDTypography color='secondary' variant='sm'>
                      {option?.length > 30 ? formatTitle(option) : option}
                    </MDTypography>
                  </MenuItem>
                ))}
              </Select>
            </MDBox>
            <MDTypography variant='sm' color='sm'>
              (Unique values for &apos;only&apos; this column will be send to
              our AI solution)
            </MDTypography>
          </MDBox>
          <MDBox
            display='flex'
            alignItems='center'
            sx={{ width: '100%', mb: 5 }}
          >
            <MDTypography variant='sm' color='sm'>
              {`Please select the max. no. of categories in which you want to categorize the data`}
            </MDTypography>
            <Select
              value={categorizationCount}
              onChange={(e: any) => setCategorizationCount(e.target.value)}
              sx={{
                minWidth: '100px',
                maxWidth: '100px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 1,
                px: 2,
                py: 1
              }}
              disabled={isAutoCategorizationLoading}
            >
              {getNumberOptions()}
            </Select>
          </MDBox>
        </MDBox>
      </DialogContent>
      <DialogActions
        sx={{ flexDirection: 'column', rowGap: 3, mb: 3 }}
        disableSpacing
      >
        <MDBox display='flex' width='100%' justifyContent='center'>
          <MDButton
            variant='gradient'
            color='primary'
            size='large'
            sx={{
              mx: 1,
              cursor: isAutoCategorizationLoading ? 'default' : 'pointer'
            }}
            onClick={() => {
              sendAmplitudeData(SUBMIT_AUTO_CATEGORIZATION, {
                number_of_categories_selected: categorizationCount,
                column_name: selectedColumn,
                is_self_chart: isSelfChart
              });
              onInitiateAutoCategorization(selectedColumn, categorizationCount);
            }}
            disableRipple={isAutoCategorizationLoading}
          >
            CONFIRM
          </MDButton>
          <MDButton
            size='large'
            color='secondary'
            variant='outlined'
            sx={{
              mx: 1,
              cursor: isAutoCategorizationLoading ? 'default' : 'pointer'
            }}
            onClick={() => (isAutoCategorizationLoading ? null : onClose())}
            disableRipple={isAutoCategorizationLoading}
          >
            CANCEL
          </MDButton>
        </MDBox>
        <span
          role='presentation'
          onClick={() =>
            sendAmplitudeData(CLICKED_KNOW_MORE, {
              'Feature Name': 'Auto Categorisation'
            })
          }
        >
          <MDTypography
            sx={{ cursor: 'pointer', fontWeight: 400, textAlign: 'center' }}
            variant='sm'
            color='secondary'
            textGradient
            component={Link}
            underline
            href='https://www.notion.so/What-is-Auto-categorization-bbd7cbef9f794edebbe25bab347cab4d'
            target='_blank'
            rel='noreferrer'
            fontWeight='medium'
          >
            Learn more about Auto-categorization
          </MDTypography>
        </span>
      </DialogActions>
      {isAutoCategorizationLoading && (
        <>
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: 'calc(50% - 15px)'
            }}
          >
            <CircularProgress color='secondary' />
          </div>
          <div
            style={{
              position: 'absolute',
              top: '60%',
              width: '100%',
              textAlign: 'center'
            }}
          >
            <MDTypography variant='xs'>
              Processing Request... Please wait. This may take up to a minute.
              Avoid refreshing or canceling. Thank you!
            </MDTypography>
          </div>
        </>
      )}
    </Dialog>
  );
};

export default AutoCategorizationModal;
